import { RapierSystemBehavior } from "./RapierSystemBehavior";
import { AttachmentRule, Behavior } from "./types";
import * as RAPIER from "@dimforge/rapier3d-compat";
import { degToRad, EulXYZ, Quat, radToDeg } from "../math";

export class TransformBehavior extends Behavior {
  static getAttachmentRules(): AttachmentRule[] {
    return [
      { type: "element", selector: "m-character" },
      { type: "element", selector: "m-cube" },
      { type: "element", selector: "m-sphere" },
      { type: "element", selector: "m-cylinder" },
      { type: "element", selector: "m-capsule" },
      { type: "element", selector: "m-box" },
      { type: "class", selector: "rigidbody" },
      { type: "class", selector: "collider" },
    ];
  }

  static elementToRigidBody: Map<Element, RAPIER.RigidBody> = new Map();
  static elementToCollider: Map<Element, RAPIER.Collider> = new Map();

  private rigidBody: RAPIER.RigidBody | null = null;
  private collider: RAPIER.Collider | null = null;
  private isInitialized: boolean = false;
  private hasRigidBody: boolean = false;
  private hasCollider: boolean = false;
  private parentRigidBody: RAPIER.RigidBody | null = null;

  // Physics parameters with defaults
  private mass: number = 1.0;
  private friction: number = 0.5;
  private restitution: number = 0.3; // bounciness
  private linearDamping: number = 0.0;
  private angularDamping: number = 0.1;
  private bodyType: "dynamic" | "kinematic" | "static" = "kinematic"; // Default to kinematic for spatial tracking
  private colliderShape: "cuboid" | "sphere" | "cylinder" | "capsule" = "cuboid";
  private lockedRotations: { x: boolean; y: boolean; z: boolean } = {
    x: false,
    y: false,
    z: false,
  };

  // Collider size parameters
  private width: number = 1.0;
  private height: number = 1.0;
  private depth: number = 1.0;
  private radius: number = 0.5;

  onAttach(): void {
    console.log("TransformBehavior attached to:", this.element);

    // Determine what this element needs
    this.hasRigidBody = this.element.classList.contains("rigidbody");
    this.hasCollider = this.shouldHaveCollider();

    console.log(
      `TransformBehavior: Element ${this.element.tagName.toLowerCase()} - hasRigidBody: ${this.hasRigidBody}, hasCollider: ${this.hasCollider}`,
    );

    // Set default parameters for m-character elements
    if (this.element.tagName.toLowerCase() === "m-character") {
      // Default to dynamic for characters unless explicitly set to something else
      this.bodyType =
        (this.element.getAttribute("body-type") as "dynamic" | "kinematic" | "static") || "dynamic";
      this.colliderShape = "capsule";
      this.height = parseFloat(this.element.getAttribute("height") || "2.0");
      this.radius = parseFloat(this.element.getAttribute("radius") || "0.5");
      this.hasRigidBody = true; // m-character needs a rigidbody for movement
      this.hasCollider = true;
    }

    if (this.hasRigidBody) {
      // Read physics configuration for rigidbody
      this.readPhysicsConfiguration();
      this.bodyType = "dynamic";
      // Process rigidbody elements immediately
      this.waitForPhysicsAndCreate();
    } else if (this.hasCollider) {
      // Read collider configuration only
      this.readColliderConfiguration();
      // Delay processing of collider-only elements to ensure parents are processed first
      setTimeout(() => this.waitForPhysicsAndCreate(), 50);
    } else {
      // Process spatial tracking elements normally
      this.waitForPhysicsAndCreate();
    }
  }

  onDetach(): void {
    console.log("TransformBehavior detached from:", this.element);
    this.cleanup();
  }

  private shouldHaveCollider(): boolean {
    // Has collider if explicitly marked with collider class
    if (this.element.classList.contains("collider")) {
      return true;
    }

    // Has collider if it's a geometric element (m-cube, m-sphere, etc.)
    const geometricElements = [
      "m-cube",
      "m-sphere",
      "m-cylinder",
      "m-capsule",
      "m-box",
      "m-character",
    ];
    return geometricElements.includes(this.element.tagName.toLowerCase());
  }

  private findParentRigidBody(): RAPIER.RigidBody | null {
    let parent = this.element.parentElement;
    while (parent) {
      const parentRigidBody = TransformBehavior.elementToRigidBody.get(parent);
      if (parentRigidBody) {
        console.log(
          `TransformBehavior: Found parent rigidbody on element ${parent.tagName.toLowerCase()} for child ${this.element.tagName.toLowerCase()}`,
        );
        return parentRigidBody;
      }
      parent = parent.parentElement;
    }
    console.log(
      `TransformBehavior: No parent rigidbody found for ${this.element.tagName.toLowerCase()}`,
    );
    return null;
  }

  private readPhysicsConfiguration(): void {
    // Read mass
    const massAttr = this.element.getAttribute("mass");
    if (massAttr) {
      const mass = parseFloat(massAttr);
      if (!isNaN(mass) && mass > 0) {
        this.mass = mass;
      }
    }

    // Read friction
    const frictionAttr = this.element.getAttribute("friction");
    if (frictionAttr) {
      const friction = parseFloat(frictionAttr);
      if (!isNaN(friction) && friction >= 0) {
        this.friction = friction;
      }
    }

    // Read restitution (bounciness)
    const restitutionAttr = this.element.getAttribute("restitution");
    if (restitutionAttr) {
      const restitution = parseFloat(restitutionAttr);
      if (!isNaN(restitution) && restitution >= 0 && restitution <= 1) {
        this.restitution = restitution;
      }
    }

    // Read linear damping
    const linearDampingAttr = this.element.getAttribute("linear-damping");
    if (linearDampingAttr) {
      const damping = parseFloat(linearDampingAttr);
      if (!isNaN(damping) && damping >= 0) {
        this.linearDamping = damping;
      }
    }

    // Read angular damping
    const angularDampingAttr = this.element.getAttribute("angular-damping");
    if (angularDampingAttr) {
      const damping = parseFloat(angularDampingAttr);
      if (!isNaN(damping) && damping >= 0) {
        this.angularDamping = damping;
      }
    }

    // Read body type
    const bodyTypeAttr = this.element.getAttribute("body-type");
    if (bodyTypeAttr && ["dynamic", "kinematic", "static"].includes(bodyTypeAttr)) {
      this.bodyType = bodyTypeAttr as "dynamic" | "kinematic" | "static";
    }

    // Read collider shape
    const shapeAttr = this.element.getAttribute("collider-shape");
    if (shapeAttr && ["cuboid", "sphere", "cylinder", "capsule"].includes(shapeAttr)) {
      this.colliderShape = shapeAttr as "cuboid" | "sphere" | "cylinder" | "capsule";
    }

    // Read rotation locks
    const lockRotationsAttr = this.element.getAttribute("lock-rotations");
    if (lockRotationsAttr) {
      const lockedAxes = lockRotationsAttr
        .toLowerCase()
        .split(",")
        .map((axis) => axis.trim());
      this.lockedRotations.x = lockedAxes.includes("x");
      this.lockedRotations.y = lockedAxes.includes("y");
      this.lockedRotations.z = lockedAxes.includes("z");
    }

    // Read size parameters
    this.readSizeParameters();
  }

  private readColliderConfiguration(): void {
    // Read friction
    const frictionAttr = this.element.getAttribute("friction");
    if (frictionAttr) {
      const friction = parseFloat(frictionAttr);
      if (!isNaN(friction) && friction >= 0) {
        this.friction = friction;
      }
    }

    // Read restitution (bounciness)
    const restitutionAttr = this.element.getAttribute("restitution");
    if (restitutionAttr) {
      const restitution = parseFloat(restitutionAttr);
      if (!isNaN(restitution) && restitution >= 0 && restitution <= 1) {
        this.restitution = restitution;
      }
    }

    // Read collider shape
    const shapeAttr = this.element.getAttribute("collider-shape");
    if (shapeAttr && ["cuboid", "sphere", "cylinder", "capsule"].includes(shapeAttr)) {
      this.colliderShape = shapeAttr as "cuboid" | "sphere" | "cylinder" | "capsule";
    }

    // Read size parameters
    this.readSizeParameters();
  }

  private readSizeParameters(): void {
    // For cuboid
    const widthAttr = this.element.getAttribute("width") || this.element.getAttribute("sx");
    if (widthAttr) {
      const width = parseFloat(widthAttr);
      if (!isNaN(width) && width > 0) {
        this.width = width;
      }
    }

    const heightAttr = this.element.getAttribute("height") || this.element.getAttribute("sy");
    if (heightAttr) {
      const height = parseFloat(heightAttr);
      if (!isNaN(height) && height > 0) {
        this.height = height;
      }
    }

    const depthAttr = this.element.getAttribute("depth") || this.element.getAttribute("sz");
    if (depthAttr) {
      const depth = parseFloat(depthAttr);
      if (!isNaN(depth) && depth > 0) {
        this.depth = depth;
      }
    }

    // For sphere/cylinder/capsule
    const radiusAttr = this.element.getAttribute("radius");
    if (radiusAttr) {
      const radius = parseFloat(radiusAttr);
      if (!isNaN(radius) && radius > 0) {
        this.radius = radius;
      }
    }
  }

  private waitForPhysicsAndCreate(): void {
    // Wait for physics system to be initialized
    const maxAttempts = 50; // 5 seconds max wait
    let attempts = 0;

    const checkPhysics = () => {
      const physicsWorld = RapierSystemBehavior.getPhysicsWorld();
      if (physicsWorld) {
        this.createPhysicsComponents();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkPhysics, 100);
      } else {
        console.error("TransformBehavior: Physics system not available after waiting");
      }
    };

    checkPhysics();
  }

  private createPhysicsComponents(): void {
    console.log(
      `TransformBehavior: Creating physics components for ${this.element.tagName.toLowerCase()}`,
      {
        hasRigidBody: this.hasRigidBody,
        hasCollider: this.hasCollider,
        elementClasses: Array.from(this.element.classList),
      },
    );

    // Step 1: Create rigidbody if needed (for spatial tracking or physics)
    if (this.hasRigidBody || !this.hasCollider) {
      this.createRigidBody();
    }

    // Step 2: Find parent rigidbody if we don't have our own
    if (!this.rigidBody && this.hasCollider) {
      this.parentRigidBody = this.findParentRigidBody();
      console.log(
        `TransformBehavior: Found parent rigidbody for ${this.element.tagName.toLowerCase()}:`,
        this.parentRigidBody ? "YES" : "NO",
      );

      // If no parent rigidbody found, try again after a short delay
      if (!this.parentRigidBody) {
        console.log(
          `TransformBehavior: No parent rigidbody found for ${this.element.tagName.toLowerCase()}, retrying...`,
        );
        setTimeout(() => this.retryParentAttachment(), 100);
        return;
      }
    }

    // Step 3: Create collider if needed
    if (this.hasCollider) {
      this.createCollider();
    }

    this.isInitialized = true;

    // Log what was created
    let description = "";
    if (this.rigidBody && this.hasRigidBody) {
      description = `rigidbody (${this.bodyType})`;
      if (this.collider) {
        description += " with collider";
      }
    } else if (this.rigidBody) {
      description = "spatial tracking rigidbody";
    } else if (this.collider && this.parentRigidBody) {
      description = "collider attached to parent rigidbody";
    } else if (this.collider) {
      description = "static collider";
    }

    console.log(
      `TransformBehavior: Created ${description} for ${this.element.tagName.toLowerCase()}`,
    );
  }

  private retryParentAttachment(attempts: number = 0): void {
    const maxRetries = 10; // Try for 1 second

    if (attempts >= maxRetries) {
      console.log(
        `TransformBehavior: Max retries reached for ${this.element.tagName.toLowerCase()}, creating static collider`,
      );
      // Create as static collider if no parent found after retries
      if (this.hasCollider) {
        this.createCollider();
      }
      this.isInitialized = true;
      return;
    }

    this.parentRigidBody = this.findParentRigidBody();

    if (this.parentRigidBody) {
      console.log(
        `TransformBehavior: Found parent rigidbody for ${this.element.tagName.toLowerCase()} on retry ${attempts + 1}`,
      );
      // Create collider now that we have a parent
      if (this.hasCollider) {
        this.createCollider();
      }
      this.isInitialized = true;

      // Log what was created
      const description =
        this.collider && this.parentRigidBody
          ? "collider attached to parent rigidbody"
          : "static collider";
      console.log(
        `TransformBehavior: Created ${description} for ${this.element.tagName.toLowerCase()}`,
      );
    } else {
      // Try again
      setTimeout(() => this.retryParentAttachment(attempts + 1), 100);
    }
  }

  private createRigidBody(): void {
    // Get initial position from element attributes
    const x = parseFloat(this.element.getAttribute("x") || "0");
    const y = parseFloat(this.element.getAttribute("y") || "0");
    const z = parseFloat(this.element.getAttribute("z") || "0");

    // Get initial rotation (in degrees)
    const rx = parseFloat(this.element.getAttribute("rx") || "0");
    const ry = parseFloat(this.element.getAttribute("ry") || "0");
    const rz = parseFloat(this.element.getAttribute("rz") || "0");

    // Create rigid body descriptor based on type
    let rigidBodyDesc: RAPIER.RigidBodyDesc;

    if (this.hasRigidBody) {
      // This is a physics rigidbody
      switch (this.bodyType) {
        case "static":
          rigidBodyDesc = RAPIER.RigidBodyDesc.fixed();
          break;
        case "kinematic":
          rigidBodyDesc = RAPIER.RigidBodyDesc.kinematicPositionBased();
          break;
        case "dynamic":
        default:
          rigidBodyDesc = RAPIER.RigidBodyDesc.dynamic();
          break;
      }

      // Set damping for physics objects
      rigidBodyDesc.setLinearDamping(this.linearDamping);
      rigidBodyDesc.setAngularDamping(this.angularDamping);
    } else {
      // This is just for spatial tracking
      rigidBodyDesc = RAPIER.RigidBodyDesc.kinematicPositionBased();
    }

    // Set position and rotation
    rigidBodyDesc.setTranslation(x, y, z);

    // Convert Euler angles to quaternion using proper math utilities
    const rotationAsEuler = new EulXYZ(degToRad(rx), degToRad(ry), degToRad(rz));
    const rotationAsQuat = new Quat().setFromEulerXYZ(rotationAsEuler);
    rigidBodyDesc.setRotation(rotationAsQuat);

    // Create the rigid body
    this.rigidBody = RapierSystemBehavior.createRigidBody(rigidBodyDesc);

    if (!this.rigidBody) {
      console.error("TransformBehavior: Failed to create rigid body");
      return;
    }

    // Apply rotation locks if any are specified
    if (this.lockedRotations.x || this.lockedRotations.y || this.lockedRotations.z) {
      // Lock specified rotation axes - invert the logic since setEnabledRotations enables rotations
      this.rigidBody.setEnabledRotations(
        !this.lockedRotations.x, // Enable X rotation if not locked
        !this.lockedRotations.y, // Enable Y rotation if not locked
        !this.lockedRotations.z, // Enable Z rotation if not locked
        true, // Wake up the rigid body
      );
      console.log(
        `TransformBehavior: Applied rotation locks for ${this.element.tagName.toLowerCase()}:`,
        {
          lockedX: this.lockedRotations.x,
          lockedY: this.lockedRotations.y,
          lockedZ: this.lockedRotations.z,
        },
      );
    }

    let lerp = this.element.querySelector("m-attr-lerp");

    if (!lerp) {
      lerp = document.createElement("m-attr-lerp");
      this.element.appendChild(lerp);
    }

    //lerp.setAttribute("attr", "x,y,z,ry");
    lerp.setAttribute("attr", "rx,ry,rz,x,y,z");
    lerp.setAttribute("duration", "50");

    // Store in static map for spatial queries
    TransformBehavior.elementToRigidBody.set(this.element, this.rigidBody);
  }

  private createCollider(): void {
    let colliderDesc: RAPIER.ColliderDesc;

    // Determine collider shape based on element type or explicit shape
    const elementType = this.element.tagName.toLowerCase();
    let shapeToUse = this.colliderShape;

    // Auto-detect shape from element type if not explicitly set
    if (!this.element.getAttribute("collider-shape")) {
      switch (elementType) {
        case "m-character":
          shapeToUse = "capsule";
          break;
        case "m-sphere":
          shapeToUse = "sphere";
          break;
        case "m-cylinder":
          shapeToUse = "cylinder";
          break;
        case "m-capsule":
          shapeToUse = "capsule";
          break;
        case "m-cube":
        case "m-box":
        default:
          shapeToUse = "cuboid";
          break;
      }
    }

    switch (shapeToUse) {
      case "sphere":
        colliderDesc = RAPIER.ColliderDesc.ball(this.radius);
        break;
      case "cylinder":
        colliderDesc = RAPIER.ColliderDesc.cylinder(this.height / 2, this.radius);
        break;
      case "capsule":
        colliderDesc = RAPIER.ColliderDesc.capsule(1.0, 0.3);
        break;
      case "cuboid":
      default:
        colliderDesc = RAPIER.ColliderDesc.cuboid(this.width / 2, this.height / 2, this.depth / 2);
        break;
    }

    // Set material properties
    colliderDesc.setFriction(this.friction);
    colliderDesc.setRestitution(this.restitution);

    // Set mass only if this collider is attached to our own rigidbody
    if (this.rigidBody && this.hasRigidBody) {
      colliderDesc.setMass(this.mass);
    }

    if (this.rigidBody && shapeToUse === "capsule") {
      colliderDesc.setTranslation(0.0, 1.3, 0.0);
      this.rigidBody.setEnabledRotations(false, false, false, true);
    }

    // Determine which rigidbody to attach to
    const targetRigidBody = this.rigidBody || this.parentRigidBody;

    // If we have a collider but no rigidbody to attach to, calculate world position for static collider
    if (!targetRigidBody) {
      const worldTransform = this.calculateWorldTransform();
      colliderDesc.setTranslation(
        worldTransform.position.x,
        worldTransform.position.y,
        worldTransform.position.z,
      );
      colliderDesc.setRotation(worldTransform.rotation);
    } else if (this.parentRigidBody && !this.rigidBody) {
      // Calculate relative transform to parent rigidbody
      const relativeTransform = this.calculateRelativeTransform();
      colliderDesc.setTranslation(
        relativeTransform.position.x,
        relativeTransform.position.y,
        relativeTransform.position.z,
      );
      colliderDesc.setRotation(relativeTransform.rotation);
    }

    // Create the collider
    this.collider = RapierSystemBehavior.createCollider(colliderDesc, targetRigidBody || undefined);

    if (!this.collider) {
      console.error("TransformBehavior: Failed to create collider");
      return;
    }

    // Store in static map
    TransformBehavior.elementToCollider.set(this.element, this.collider);
  }

  private calculateWorldTransform(): {
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number; w: number };
  } {
    // Get local transform
    const x = parseFloat(this.element.getAttribute("x") || "0");
    const y = parseFloat(this.element.getAttribute("y") || "0");
    const z = parseFloat(this.element.getAttribute("z") || "0");
    const rx = parseFloat(this.element.getAttribute("rx") || "0");
    const ry = parseFloat(this.element.getAttribute("ry") || "0");
    const rz = parseFloat(this.element.getAttribute("rz") || "0");

    // Apply parent transforms
    let worldPosition = { x, y, z };
    let worldRotation = { x: rx, y: ry, z: rz };

    let parent = this.element.parentElement;
    while (parent) {
      const parentRigidBody = TransformBehavior.elementToRigidBody.get(parent);
      if (parentRigidBody) {
        const parentPos = parentRigidBody.translation();
        const parentRot = parentRigidBody.rotation();
        const parentQuat = new Quat(parentRot.x, parentRot.y, parentRot.z, parentRot.w);
        const parentEuler = new EulXYZ().setFromQuaternion(parentQuat);

        // Apply parent rotation to local position
        const parentYaw = parentEuler.y;
        const cosY = Math.cos(parentYaw);
        const sinY = Math.sin(parentYaw);

        const rotatedX = worldPosition.x * cosY - worldPosition.z * sinY;
        const rotatedZ = worldPosition.x * sinY + worldPosition.z * cosY;

        worldPosition = {
          x: parentPos.x + rotatedX,
          y: parentPos.y + worldPosition.y,
          z: parentPos.z + rotatedZ,
        };

        worldRotation = {
          x: radToDeg(parentEuler.x) + worldRotation.x,
          y: radToDeg(parentEuler.y) + worldRotation.y,
          z: radToDeg(parentEuler.z) + worldRotation.z,
        };
        break; // Only apply immediate parent transform for now
      }
      parent = parent.parentElement;
    }

    const finalRotationEuler = new EulXYZ(
      degToRad(worldRotation.x),
      degToRad(worldRotation.y),
      degToRad(worldRotation.z),
    );
    const finalRotationQuat = new Quat().setFromEulerXYZ(finalRotationEuler);

    return {
      position: worldPosition,
      rotation: finalRotationQuat,
    };
  }

  private calculateRelativeTransform(): {
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number; w: number };
  } {
    // For now, use local transform as relative transform
    // This could be enhanced to calculate proper relative transforms
    const x = parseFloat(this.element.getAttribute("x") || "0");
    const y = parseFloat(this.element.getAttribute("y") || "0");
    const z = parseFloat(this.element.getAttribute("z") || "0");
    const rx = parseFloat(this.element.getAttribute("rx") || "0");
    const ry = parseFloat(this.element.getAttribute("ry") || "0");
    const rz = parseFloat(this.element.getAttribute("rz") || "0");

    const rotationEuler = new EulXYZ(degToRad(rx), degToRad(ry), degToRad(rz));
    const rotationQuat = new Quat().setFromEulerXYZ(rotationEuler);

    return {
      position: { x, y, z },
      rotation: rotationQuat,
    };
  }

  private cleanup(): void {
    if (this.collider) {
      RapierSystemBehavior.removeCollider(this.collider);
      TransformBehavior.elementToCollider.delete(this.element);
      this.collider = null;
    }

    if (this.rigidBody) {
      RapierSystemBehavior.removeRigidBody(this.rigidBody);
      TransformBehavior.elementToRigidBody.delete(this.element);
      this.rigidBody = null;
    }

    this.parentRigidBody = null;
    this.isInitialized = false;
  }

  tick(): void {
    if (!this.isInitialized) return;

    // Only update rigidbody if we own one
    if (!this.rigidBody) return;

    if (this.hasRigidBody && this.bodyType === "dynamic") {
      // For dynamic physics objects, sync physics position back to element attributes
      const position = this.rigidBody.translation();
      const rotation = this.rigidBody.rotation();

      // Update element position
      this.element.setAttribute("x", position.x.toFixed(3));
      this.element.setAttribute("y", position.y.toFixed(3));
      this.element.setAttribute("z", position.z.toFixed(3));

      // Convert quaternion to Euler angles (in degrees) using proper math utilities
      const quat = new Quat(rotation.x, rotation.y, rotation.z, rotation.w);
      const euler = new EulXYZ().setFromQuaternion(quat);
      this.element.setAttribute("rx", radToDeg(euler.x).toFixed(3));
      this.element.setAttribute("ry", radToDeg(euler.y).toFixed(3));
      this.element.setAttribute("rz", radToDeg(euler.z).toFixed(3));
    } else {
      // For kinematic objects (including non-physics elements and kinematic physics objects)
      // and static physics objects, read element attributes and update rigid body
      const x = parseFloat(this.element.getAttribute("x") || "0");
      const y = parseFloat(this.element.getAttribute("y") || "0");
      const z = parseFloat(this.element.getAttribute("z") || "0");

      const rx = parseFloat(this.element.getAttribute("rx") || "0");
      const ry = parseFloat(this.element.getAttribute("ry") || "0");
      const rz = parseFloat(this.element.getAttribute("rz") || "0");

      // Handle parent-child relationships
      const parentElement = this.element.parentElement;
      let worldPosition = { x, y, z };
      let worldRotation = { x: rx, y: ry, z: rz };

      // If parent has a transform behavior, apply its transform
      if (parentElement && TransformBehavior.elementToRigidBody.has(parentElement)) {
        const parentRigidBody = TransformBehavior.elementToRigidBody.get(parentElement)!;
        const parentPos = parentRigidBody.translation();
        const parentRot = parentRigidBody.rotation();

        // Convert parent rotation to Euler for combination using proper math utilities
        const parentQuat = new Quat(parentRot.x, parentRot.y, parentRot.z, parentRot.w);
        const parentEuler = new EulXYZ().setFromQuaternion(parentQuat);

        // Apply parent transform to local position
        // For proper transform hierarchy, we need to apply rotation to the local position
        // and then add the parent's position

        // Create a simple rotation matrix for Y rotation (most common case)
        const parentYaw = parentEuler.y;
        const cosY = Math.cos(parentYaw);
        const sinY = Math.sin(parentYaw);

        // Rotate local position by parent rotation
        const rotatedX = x * cosY - z * sinY;
        const rotatedZ = x * sinY + z * cosY;

        worldPosition = {
          x: parentPos.x + rotatedX,
          y: parentPos.y + y, // Y is typically up, so just add
          z: parentPos.z + rotatedZ,
        };

        worldRotation = {
          x: radToDeg(parentEuler.x) + rx,
          y: radToDeg(parentEuler.y) + ry,
          z: radToDeg(parentEuler.z) + rz,
        };

        // Update the element's attributes to reflect the world position
        // This ensures the visual representation shows the correct position
        this.element.setAttribute("x", worldPosition.x.toFixed(3));
        this.element.setAttribute("y", worldPosition.y.toFixed(3));
        this.element.setAttribute("z", worldPosition.z.toFixed(3));
        this.element.setAttribute("rx", worldRotation.x.toFixed(3));
        this.element.setAttribute("ry", worldRotation.y.toFixed(3));
        this.element.setAttribute("rz", worldRotation.z.toFixed(3));
      }

      // Update rigid body position and rotation
      this.rigidBody.setTranslation(worldPosition, true);

      const rotationEuler = new EulXYZ(
        degToRad(worldRotation.x),
        degToRad(worldRotation.y),
        degToRad(worldRotation.z),
      );
      const rotationQuat = new Quat().setFromEulerXYZ(rotationEuler);
      this.rigidBody.setRotation(rotationQuat, true);
    }

    // Update collider position if it's attached to a parent rigidbody
    if (this.collider && this.parentRigidBody && !this.rigidBody) {
      // For colliders attached to parent rigidbodies, we might need to update their relative position
      // This is more complex and might require recreating the collider
      // For now, we'll leave this as a future enhancement
    }
  }

  onAttributeChange(attributeName: string, oldValue: string | null, newValue: string | null): void {
    // Handle changes to size attributes that affect collider bounds
    const sizeAttributes = ["sx", "sy", "sz", "width", "height", "depth", "radius"];

    if (sizeAttributes.includes(attributeName)) {
      // Update size parameters and collider bounds
      this.readSizeParameters();
      this.updateColliderBounds();
      return;
    }

    // Handle changes to physics-related attributes
    const physicsAttributes = [
      "mass",
      "friction",
      "restitution",
      "linear-damping",
      "angular-damping",
      "body-type",
      "collider-shape",
    ];

    if (physicsAttributes.includes(attributeName)) {
      // Recreate the physics components with new parameters
      this.cleanup();
      if (this.hasRigidBody) {
        this.readPhysicsConfiguration();
      } else if (this.hasCollider) {
        this.readColliderConfiguration();
      }
      this.waitForPhysicsAndCreate();
    }
  }

  private updateColliderBounds(): void {
    if (!this.collider || !this.isInitialized) {
      return;
    }

    console.log(
      `TransformBehavior: Updating collider bounds for ${this.element.tagName.toLowerCase()}`,
      {
        width: this.width,
        height: this.height,
        depth: this.depth,
        radius: this.radius,
        shape: this.colliderShape,
      },
    );

    // Get the current collider's rigidbody (if any)
    const targetRigidBody = this.rigidBody || this.parentRigidBody;

    // Store current transform information
    let currentTransform = null;
    if (!targetRigidBody) {
      // For static colliders, we need to preserve the world transform
      const currentPos = this.collider.translation();
      const currentRot = this.collider.rotation();
      currentTransform = {
        position: { x: currentPos.x, y: currentPos.y, z: currentPos.z },
        rotation: { x: currentRot.x, y: currentRot.y, z: currentRot.z, w: currentRot.w },
      };
    } else if (this.parentRigidBody && !this.rigidBody) {
      // For colliders attached to parent rigidbodies, preserve relative transform
      currentTransform = this.calculateRelativeTransform();
    }

    // Remove the old collider
    RapierSystemBehavior.removeCollider(this.collider);
    TransformBehavior.elementToCollider.delete(this.element);

    // Create new collider descriptor with updated bounds
    let colliderDesc: RAPIER.ColliderDesc;

    // Determine collider shape based on element type or explicit shape
    const elementType = this.element.tagName.toLowerCase();
    let shapeToUse = this.colliderShape;

    // Auto-detect shape from element type if not explicitly set
    if (!this.element.getAttribute("collider-shape")) {
      switch (elementType) {
        case "m-character":
          shapeToUse = "capsule";
          break;
        case "m-sphere":
          shapeToUse = "sphere";
          break;
        case "m-cylinder":
          shapeToUse = "cylinder";
          break;
        case "m-capsule":
          shapeToUse = "capsule";
          break;
        case "m-cube":
        case "m-box":
        default:
          shapeToUse = "cuboid";
          break;
      }
    }

    switch (shapeToUse) {
      case "sphere":
        colliderDesc = RAPIER.ColliderDesc.ball(this.radius);
        break;
      case "cylinder":
        colliderDesc = RAPIER.ColliderDesc.cylinder(this.height / 2, this.radius);
        break;
      case "capsule":
        colliderDesc = RAPIER.ColliderDesc.capsule(this.height / 2, this.radius);
        break;
      case "cuboid":
      default:
        colliderDesc = RAPIER.ColliderDesc.cuboid(this.width / 2, this.height / 2, this.depth / 2);
        break;
    }

    // Set material properties
    colliderDesc.setFriction(this.friction);
    colliderDesc.setRestitution(this.restitution);

    // Set mass only if this collider is attached to our own rigidbody
    if (this.rigidBody && this.hasRigidBody) {
      colliderDesc.setMass(this.mass);
    }

    // Apply stored transform if needed
    if (currentTransform) {
      colliderDesc.setTranslation(
        currentTransform.position.x,
        currentTransform.position.y,
        currentTransform.position.z,
      );
      colliderDesc.setRotation(currentTransform.rotation);
    }

    // Create the new collider
    this.collider = RapierSystemBehavior.createCollider(colliderDesc, targetRigidBody || undefined);

    if (!this.collider) {
      console.error("TransformBehavior: Failed to recreate collider with new bounds");
      return;
    }

    // Store in static map
    TransformBehavior.elementToCollider.set(this.element, this.collider);

    console.log(
      `TransformBehavior: Successfully updated collider bounds for ${this.element.tagName.toLowerCase()}`,
    );
  }

  getNearbyElementsWithClass(className: string, range: number = 3.0): Element[] {
    if (!this.rigidBody) return [];

    const nearbyElements: Element[] = [];
    const myPosition = this.rigidBody.translation();

    // Iterate through all elements in the Rapier spatial index
    TransformBehavior.elementToRigidBody.forEach((rigidBody, element) => {
      // Skip the current element
      if (element === this.element) {
        return;
      }

      const otherPosition = rigidBody.translation();
      const distance = Math.sqrt(
        Math.pow(myPosition.x - otherPosition.x, 2) +
        Math.pow(myPosition.y - otherPosition.y, 2) +
        Math.pow(myPosition.z - otherPosition.z, 2),
      );

      if (distance > range) {
        return;
      }

      const behavior = element.getAttribute("behaviour");
      if (behavior) {
        const behaviors = behavior.split(" ");
        if (behaviors.includes(className)) {
          nearbyElements.push(element);
        }
      }
    });

    return nearbyElements;
  }

  // Public methods for other behaviors to interact with this rigid body
  getRigidBody(): RAPIER.RigidBody | null {
    return this.rigidBody;
  }

  getCollider(): RAPIER.Collider | null {
    return this.collider;
  }

  getParentRigidBody(): RAPIER.RigidBody | null {
    return this.parentRigidBody;
  }

  hasOwnRigidBody(): boolean {
    return this.hasRigidBody;
  }

  hasOwnCollider(): boolean {
    return this.hasCollider;
  }

  // Physics interaction methods (only work for rigidbody objects)
  applyForce(
    force: { x: number; y: number; z: number },
    point?: { x: number; y: number; z: number },
  ): void {
    if (!this.rigidBody || !this.hasRigidBody) return;

    if (point) {
      this.rigidBody.addForceAtPoint(force, point, true);
    } else {
      this.rigidBody.addForce(force, true);
    }
  }

  applyImpulse(
    impulse: { x: number; y: number; z: number },
    point?: { x: number; y: number; z: number },
  ): void {
    if (!this.rigidBody || !this.hasRigidBody) return;

    if (point) {
      this.rigidBody.applyImpulseAtPoint(impulse, point, true);
    } else {
      this.rigidBody.applyImpulse(impulse, true);
    }
  }

  setVelocity(velocity: { x: number; y: number; z: number }): void {
    if (!this.rigidBody || !this.hasRigidBody) return;
    this.rigidBody.setLinvel(velocity, true);
  }

  setAngularVelocity(angularVelocity: { x: number; y: number; z: number }): void {
    if (!this.rigidBody || !this.hasRigidBody) return;
    this.rigidBody.setAngvel(angularVelocity, true);
  }

  setPosition(position: { x: number; y: number; z: number }): void {
    if (!this.rigidBody || !this.hasRigidBody) return;
    this.rigidBody.setTranslation(position, true);
  }

  // Static methods to get rigid body or collider for any element
  static getRigidBodyForElement(element: Element): RAPIER.RigidBody | null {
    return TransformBehavior.elementToRigidBody.get(element) || null;
  }

  static getColliderForElement(element: Element): RAPIER.Collider | null {
    return TransformBehavior.elementToCollider.get(element) || null;
  }

  // Static method to find the effective rigidbody for an element (its own or parent's)
  static getEffectiveRigidBodyForElement(element: Element): RAPIER.RigidBody | null {
    // First check if element has its own rigidbody
    const ownRigidBody = TransformBehavior.elementToRigidBody.get(element);
    if (ownRigidBody) {
      return ownRigidBody;
    }

    // Otherwise, search up the parent hierarchy
    let parent = element.parentElement;
    while (parent) {
      const parentRigidBody = TransformBehavior.elementToRigidBody.get(parent);
      if (parentRigidBody) {
        return parentRigidBody;
      }
      parent = parent.parentElement;
    }

    return null;
  }
}
